import { clearError } from '#app'
import { nextTick, watch } from 'vue'

export default defineNuxtPlugin(async nuxtApp => {  

  const router = import.meta.client ? useRouter() : null
  const POPUP_MESSAGE = 'Network error. Please refresh the page.';

  // Patterns that warrant showing a "reload" popup
  const RELOAD_ERROR_RE =
    /Couldn't resolve component|Loading (?:CSS )?chunk|ChunkLoadError|failed to fetch dynamically imported module|Export not found|Importing a module script failed|Unexpected EOF|Unexpected token </i

  const shouldShowReload = (e: unknown): boolean => {
    const msg = String((e as any)?.message || e)
    return RELOAD_ERROR_RE.test(msg)
  }

  // Watch Nuxt's reactive error ref and clear it as soon as it appears
  const nuxtErrRef = useError()

  watch(nuxtErrRef, (errVal) => {
    if (!errVal) return

    console.log('[watch useError()]', errVal)

    const data = {
      message: errVal?.message,
      name: errVal?.name,
      stack: errVal?.stack,
    }

    // Log the error
    err.send(data, 'watch:useError')

    // Inform the user
    if (import.meta.client && shouldShowReload(errVal)) {
      nuxtApp.$popup?.error?.(POPUP_MESSAGE);
    }

    // Clear the error so Nuxt doesn't render error.vue
    nuxtErrRef.value = null
    clearError()

    // Optionally re‑render current route
    nextTick(() => {
      router?.replace(router.currentRoute.value.fullPath)
    })
  })

  // Global Vue error handler – fires before Nuxt shows error.vue
  nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
    console.log('[global errorHandler]', error, info)

    const data = {
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
    }

    // Send the error to backend
    err.send(data, 'vue:errorHandler', info)

    // Let the user know but keep the UI running
    if (import.meta.client && shouldShowReload(error)) {
      nuxtApp.$popup?.error?.(POPUP_MESSAGE);
    }

    // Clear Nuxt global error (if any) to avoid full‑screen error.vue
    try {
      const nuxtErr = useError()
      nuxtErr.value = null
      clearError()
    } catch { /* ignored */ }
  }
  

  const err = {
    send: (bd, hk) => {
      const body = typeof bd === 'string' ? bd : JSON.stringify(bd)

      nuxtApp.$api.postErrorNx({
        message: body,
        curUrl: err.getCurUrl(),
        prevUrl: err.getPrevUrl(),
        moment: hk,
        process: import.meta.server ? 'server' : 'client',
        userAgent: import.meta.client ? navigator?.userAgent : '-',
        user: useAuth().getId(),
        pixelRatio: import.meta.client ? window.devicePixelRatio : '-',
        width: import.meta.client ? window.innerWidth : '-',
        height: import.meta.client ? window.innerHeight : '-',
        storeKeys: err.getStoreKeys(),
        cookieKeys: err.getCookieKeys(),
        authToken: useCook('auth:token',{maxAge: 60 * 60 * 24 * 31 * 6, watch: 'shallow'}).value,
        authGuest: useCook('auth:guest',{maxAge: 60 * 60 * 24 * 31 * 6, watch: 'shallow'}).value,
      }).catch(() => {})
    },
    getCookieKeys: () => {
      try {
        if (import.meta.client && document.cookie) {
          const keys = document.cookie
            .split(';')
            .map(item => item.split('=')[0].trim())
          return keys.length ? keys.join(', ') : '-'
        }
      } catch {}
      return '-'
    },
    getStoreKeys: () => {
      try {
        if (import.meta.client && window?.localStorage) {
          const keys = Object.keys(localStorage)
          return keys.length ? keys.join(', ') : '-'
        }
      } catch {}
      return '-'
    },
    getPrevUrl: () => {
      if (!router?.options?.history) return '-'
      return router.options.history.state?.back || '-'
    },
    getCurUrl: () => {
      if (!router?.options?.history) return '-'
      return router.options.history.state?.current || '-'
    },
  }


  // === extra handlers for chunk‑load and global errors =======================
  const isChunkError = (e: unknown): boolean => {
    const msg = String((e as any)?.message || e)
    return /ChunkLoadError|Loading chunk|failed to fetch dynamically imported module|Unexpected token </i.test(msg)
  }

  const handleFatalLoadError = (e: unknown, hook: string) => {
    err.send(e, hook)
    if (import.meta.client && shouldShowReload(e)) {
      nuxtApp.$popup?.error?.(POPUP_MESSAGE)
    }
  }

  // router‑level dynamic‑import errors
  router?.onError(err => {
    if (isChunkError(err)) handleFatalLoadError(err, 'router:onError')
  })

  // global resource / promise errors in browser
  if (import.meta.client) {
    window.addEventListener(
      'error',
      (e: ErrorEvent) => {
        let payload: any = e.error

        // If it's a resource loading error, e.error is undefined.
        // Extract info about the element that failed to load.
        if (!payload) {
          const target = e.target as HTMLElement | null

          if (
            target &&
            (target.tagName === 'LINK' || target.tagName === 'IMG')
          ) {
            return
          }else if (
            target &&
            (target.tagName === 'SCRIPT')
          ) {
            payload = {
              tag: target.tagName,
              src: (target as HTMLScriptElement | HTMLImageElement).src || '',
              href: (target as HTMLLinkElement).href || '',
              outerHTML: target.outerHTML.slice(0, 200),
            }
          } else {
            // Fallback: stringify the full ErrorEvent
            payload = {
              message: e.message,
              filename: e.filename,
              lineno: e.lineno,
              colno: e.colno,
              type: e.type,
            }
          }
        }

        handleFatalLoadError(payload, 'window:error')
      },
      true
    )
    window.addEventListener(
      'unhandledrejection',
      (e: PromiseRejectionEvent) =>
        handleFatalLoadError(e.reason, 'window:unhandledrejection')
    )
  }
  // ==========================================================================  


  // nuxtApp.hook('app:error', (error) => {
  //   // console.log('app:error');
  //   console.log(error); 
  //   var data = {
  //     message: error?.message,
  //     name: error?.name,
  //     stack: error?.stack,
  //   };
  //   err.send(data, 'app:error');
  //   // Reset Nuxt error state and re‑render current route (prevents error.vue)
  //   nextTick(() => {
  //     clearError({ redirect: router.currentRoute.value.fullPath })
  //   })
  // });


 

  return {
    provide: {
      err: err,
    },
  };

})